import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api, clientId } from "@/app/api/client"

// Lease Intake — upload a recorded Assignment (or OGL / CSV), let the server
// extract leases into staged rows, review/edit them here, then commit them to
// the Master Lease Schedule through LeaseImportService. Mirrors the "Run JSON"
// contract of Api::V1::LeaseIntakesController (see the lease-intake plan).

export type LeaseIntakeStatus =
  "queued" | "extracting" | "review" | "committing" | "committed" | "discarded" | "failed"

export type LeaseIntakeDocKind = "pdf" | "image" | "csv" | "docx"

export type LeaseIntakeStage = "survey" | "slices" | "verify" | "done"

export interface LeaseIntakeProgress {
  stage?: LeaseIntakeStage
  current?: number
  total?: number
  found?: number
}

// The recorded instrument the leases were extracted from. Every key is
// optional/nullable: CSV and OGL runs leave most of it blank.
export interface LeaseIntakeHeader {
  source_book?: string | null
  source_page?: string | null
  assignor?: string | null
  assignee?: string | null
  county?: string | null
  state?: string | null
  dated?: string | null
  effective?: string | null
  recorded?: string | null
}

export type LeaseIntakeHeaderKey = keyof LeaseIntakeHeader

/** The editable text fields of a staged row (strings as extracted/edited). */
export interface LeaseIntakeRowFields {
  lessor: string | null
  lessee: string | null
  lease_date: string | null
  book: string | null
  page: string | null
  county: string | null
  str: string | null
  legal: string | null
}

export type LeaseIntakeRowField = keyof LeaseIntakeRowFields

export interface LeaseIntakeRow extends LeaseIntakeRowFields {
  id: string
  position: number
  include: boolean
  flags: string[]
  source_pages: string | null
  /** Computed on read: a kept Lease with the same dedup identity already exists. */
  on_file: boolean
}

export interface LeaseIntakeConflict {
  key?: string
  lease_id?: string
  lessor: string | null
  field: string
  existing: string | null
  incoming: string | null
}

export interface LeaseIntakeError {
  lessor?: string | null
  reason: string
}

/** LeaseImportService's result, stored on commit. */
export interface LeaseIntakeSummary {
  inserted: number
  updated: number
  skipped: number
  conflicts: LeaseIntakeConflict[]
  errors: LeaseIntakeError[]
}

export interface LeaseIntakeUsage {
  calls?: number
  input_tokens?: number
  output_tokens?: number
  cache_read_input_tokens?: number
}

export interface LeaseIntakeRun {
  id: string
  filename: string
  doc_kind: LeaseIntakeDocKind
  status: LeaseIntakeStatus
  page_count: number | null
  progress: LeaseIntakeProgress
  error: string | null
  header: LeaseIntakeHeader
  estimated_leases: number | null
  notes: string[]
  rows: LeaseIntakeRow[]
  summary: LeaseIntakeSummary | null
  usage: LeaseIntakeUsage
  created_at: string
}

/** GET /lease_intakes list entry (newest first, 20). */
export interface LeaseIntakeRunListItem {
  id: string
  filename: string
  status: LeaseIntakeStatus
  doc_kind: LeaseIntakeDocKind
  row_count: number
  created_at: string
}

/** A row in a PATCH: `id` absent → new row; `_destroy` → remove it. */
export type LeaseIntakeRowPatch = Partial<LeaseIntakeRowFields> & {
  id?: string
  _destroy?: boolean
  include?: boolean
}

export interface LeaseIntakePatch {
  header?: LeaseIntakeHeader
  rows?: LeaseIntakeRowPatch[]
}

// In-flight statuses: the run page shows progress and polls. Every other
// status (review, committed, discarded, failed) is settled and stops polling —
// a run whose worker died is failed by the server's stale sweep, which ends
// the polling too.
export const ACTIVE_STATUSES: LeaseIntakeStatus[] = ["queued", "extracting", "committing"]

const listKey = ["lease_intakes", clientId] as const
const runKey = (id: string) => ["lease_intake", clientId, id] as const

export function useLeaseIntakes() {
  return useQuery<LeaseIntakeRunListItem[]>({
    queryKey: listKey,
    queryFn: async () => (await api.get<{ runs: LeaseIntakeRunListItem[] }>("/lease_intakes")).runs,
  })
}

export function useLeaseIntake(id: string | undefined) {
  return useQuery<LeaseIntakeRun>({
    queryKey: runKey(id ?? ""),
    queryFn: () => api.get<LeaseIntakeRun>(`/lease_intakes/${id}`),
    enabled: !!id,
    // Poll while the extraction job is working; stop once it settles.
    refetchInterval: (query) =>
      query.state.data && ACTIVE_STATUSES.includes(query.state.data.status) ? 2000 : false,
  })
}

export function useUploadLeaseIntake() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.append("file", file)
      return api.postForm<LeaseIntakeRun>("/lease_intakes", form)
    },
    onSuccess: (run) => {
      queryClient.setQueryData(runKey(run.id), run)
      queryClient.invalidateQueries({ queryKey: ["lease_intakes"] })
    },
  })
}

// PATCH, commit, and discard all answer with the full run JSON, which is
// written straight into the run's cache entry.
function useRunMutation<V>(request: (vars: V) => Promise<LeaseIntakeRun>, alsoInvalidate = false) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: request,
    onSuccess: (run) => {
      if (run?.id) queryClient.setQueryData(runKey(run.id), run)
      queryClient.invalidateQueries({ queryKey: ["lease_intake"] })
      queryClient.invalidateQueries({ queryKey: ["lease_intakes"] })
      if (alsoInvalidate) queryClient.invalidateQueries({ queryKey: ["leases"] })
    },
  })
}

export function useUpdateLeaseIntake() {
  return useRunMutation(({ id, patch }: { id: string; patch: LeaseIntakePatch }) =>
    api.patch<LeaseIntakeRun>(`/lease_intakes/${id}`, patch)
  )
}

export function useCommitLeaseIntake() {
  return useRunMutation(
    (id: string) => api.post<LeaseIntakeRun>(`/lease_intakes/${id}/commit`),
    true
  )
}

export function useDiscardLeaseIntake() {
  return useRunMutation((id: string) => api.post<LeaseIntakeRun>(`/lease_intakes/${id}/discard`))
}
